import { Button } from "@aircall/ds";
import { useTranslation } from "react-i18next";
import { ScorecardPreCreateModal } from "@/components/ScorecardPreCreateModal/ScorecardPreCreateModal";
import { SCORECARDS_ROUTE } from "@/constants/routes.constants";
import { ScoreCardFragment } from "@/graphql/generated/graphql";
import { redirectToDashboard } from "@/helpers/navigation/navigation.helpers";
import { useCreateScoreCardFlow } from "@/hooks/useCreateScoreCardFlow/useCreateScoreCardFlow";
import { useGetScorecards } from "@/hooks/useGetScorecards/useGetScorecards";
import { EvaluationEmptyState } from "../EvaluationEmptyState/EvaluationEmptyState";
import { LoadingView } from "../LoadingView/LoadingView";
import { ScoreCardEmptyDropdownMenu } from "../ScoreCardEmptyDropdownMenu/ScoreCardEmptyDropdownMenu";
import { ScoreCardMenu } from "../ScoreCardMenu/ScoreCardMenu";
import { useTracking } from "./EvaluationScorecards.hooks";

function ScorecardsList({ scorecards }: { scorecards: ScoreCardFragment[] }) {
  const { t } = useTranslation();
  const { onClick: handleOnClick } = useTracking();

  const handleOnClickManage = () => {
    redirectToDashboard(SCORECARDS_ROUTE);
  };

  return (
    <div
      className="flex h-full flex-1 flex-col items-start gap-3 px-6"
      data-test="evaluation-empty-score-card-list"
    >
      <div className="flex w-full items-center justify-between">
        <p className="text-sm font-semibold">
          {`${t("evaluation.empty_view.select_scorecard")}:`}
        </p>
        <Button
          variant="link"
          size="lg"
          onClick={handleOnClickManage}
          data-test="manage-scorecards-button"
        >
          {t("evaluation.empty_view.manage_scorecard")}
        </Button>
      </div>
      <ScoreCardMenu data={scorecards} onClickItem={handleOnClick} />
    </div>
  );
}

/**
 * fetch the scorecard list
 * render empty view if the list is empty, otherwise render the score card list
 * @returns
 */
export function EvaluationScorecards() {
  const { t } = useTranslation();
  const handleOnClickManage = () => {
    redirectToDashboard(SCORECARDS_ROUTE);
  };
  const {
    isPreCreateModalOpen,
    hideModal,
    onPreCreateFormSubmit,
    onCreateButtonClick,
  } = useCreateScoreCardFlow();

  const { loading, scorecards } = useGetScorecards({ isPublished: true });

  if (loading) {
    return <LoadingView className="mt-0 h-full" />;
  }

  if (!scorecards || scorecards.length === 0) {
    return (
      <>
        <EvaluationEmptyState title={t("evaluation.empty_view.title")}>
          <div className="flex flex-col items-center gap-2">
            <ScoreCardEmptyDropdownMenu
              onCreateButtonClick={onCreateButtonClick}
            />
            <Button
              variant="link"
              size="lg"
              onClick={handleOnClickManage}
              data-test="manage-scorecards-button"
            >
              {t("evaluation.empty_view.manage_scorecard")}
            </Button>
          </div>
        </EvaluationEmptyState>
        <ScorecardPreCreateModal
          show={isPreCreateModalOpen}
          onHide={hideModal}
          onSubmit={onPreCreateFormSubmit}
        />
      </>
    );
  }

  return <ScorecardsList scorecards={scorecards} />;
}
